import type { HaloV2StoreCoverage } from '@halo/web-types';

export const LAYERS = ['raw', 'adjusted', 'confidence', 'events', 'pressure'] as const;
export type Layer = (typeof LAYERS)[number];

const LAYER_LABELS: Record<Layer, string> = {
  raw: 'Raw baseline',
  adjusted: 'Media-adjusted',
  confidence: 'Confidence',
  events: 'Event days',
  pressure: 'Media pressure',
};

const PRESETS = [
  { label: '90d', days: 90 },
  { label: '180d', days: 180 },
  { label: '365d', days: 365 },
] as const;

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export interface ControlsState {
  storeId: number;
  platform: string;
  start: string;
  end: string;
  layers: ReadonlySet<Layer>;
}

interface ControlsProps {
  stores: HaloV2StoreCoverage[];
  storeNames: Map<number, string> | undefined;
  state: ControlsState;
  onChange: (next: ControlsState) => void;
  coverage: { firstDate: string; lastDate: string; rowCount: number } | undefined;
}

const inputCls =
  'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';

export function Controls({ stores, storeNames, state, onChange, coverage }: ControlsProps) {
  const selected = stores.find((s) => s.storeId === state.storeId);
  const platforms = selected?.platforms ?? [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <select
          className={inputCls}
          value={state.storeId}
          onChange={(e) => {
            const storeId = Number(e.target.value);
            const next = stores.find((s) => s.storeId === storeId);
            onChange({ ...state, storeId, platform: next?.platforms[0] ?? state.platform });
          }}
        >
          <option value={0}>Select store…</option>
          {stores.map((s) => (
            <option key={s.storeId} value={s.storeId}>
              {storeNames?.get(s.storeId) ?? `Store ${s.storeId}`} (#{s.storeId})
            </option>
          ))}
        </select>

        <select
          className={inputCls}
          value={state.platform}
          disabled={platforms.length === 0}
          onChange={(e) => onChange({ ...state, platform: e.target.value })}
        >
          {platforms.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <input
          type="date"
          className={inputCls}
          value={state.start}
          onChange={(e) => onChange({ ...state, start: e.target.value })}
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="date"
          className={inputCls}
          value={state.end}
          onChange={(e) => onChange({ ...state, end: e.target.value })}
        />
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className="rounded-md border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => onChange({ ...state, start: isoDaysAgo(p.days), end: isoDaysAgo(1) })}
          >
            {p.label}
          </button>
        ))}

        <div className="flex items-center gap-2 ml-auto">
          {LAYERS.map((layer) => (
            <label
              key={layer}
              className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300"
            >
              <input
                type="checkbox"
                checked={state.layers.has(layer)}
                onChange={(e) => {
                  const layers = new Set(state.layers);
                  if (e.target.checked) layers.add(layer);
                  else layers.delete(layer);
                  onChange({ ...state, layers });
                }}
              />
              {LAYER_LABELS[layer]}
            </label>
          ))}
        </div>
      </div>

      {state.storeId !== 0 ? (
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {coverage ? (
            <>
              computed:{' '}
              <span className="text-gray-500 dark:text-gray-400">{coverage.firstDate}</span>
              {' → '}
              <span className="text-gray-500 dark:text-gray-400">{coverage.lastDate}</span>{' '}
              <span>({coverage.rowCount} computed days)</span>
            </>
          ) : (
            'no computed data yet — run a backfill'
          )}
        </div>
      ) : null}
    </div>
  );
}
